import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CreateMapArrowInput,
  CreateMapObjectInput,
  MapArrowDTO,
  MapMode,
  MapObjectDTO,
  OperationPlanDTO,
  PatchMapArrowInput,
  PatchMapObjectInput,
  PatchOperationPlanInput,
  PatchWarPlanInput,
  WarPlanDTO,
} from '@warplan/shared';
import { DEFAULT_OPERATION_PLAN, DEFAULT_WAR_PLAN } from '@warplan/shared';

/**
 * Local-only "sandbox" map state, partitioned by flavour (planner/operation).
 *
 * - 100% client-side: never POSTed to the backend.
 * - Persisted in localStorage under one key per flavour, so switching
 *   between sandbox flavours preserves each one independently.
 * - Available to every authenticated role (User / Editor / Admin).
 *
 * The shape mirrors the live REST payloads so the same UI components can
 * render either source (see `lib/map-data-source.ts`).
 */
export interface SandboxSlice {
  objects: MapObjectDTO[];
  arrows: MapArrowDTO[];
  /** Only used by the `planner` flavour. */
  warPlan: WarPlanDTO;
  /** Only used by the `operation` flavour. */
  operationPlan: OperationPlanDTO;
}

interface SandboxStore {
  planner: SandboxSlice;
  operation: SandboxSlice;
  // ---- Object operations -------------------------------------------------
  createObject: (flavour: MapMode, input: CreateMapObjectInput) => MapObjectDTO;
  patchObject: (flavour: MapMode, id: string, patch: PatchMapObjectInput) => MapObjectDTO | null;
  deleteObject: (flavour: MapMode, id: string) => void;
  // ---- Arrow operations --------------------------------------------------
  createArrow: (flavour: MapMode, input: CreateMapArrowInput) => MapArrowDTO;
  patchArrow: (flavour: MapMode, id: string, patch: PatchMapArrowInput) => MapArrowDTO | null;
  deleteArrow: (flavour: MapMode, id: string) => void;
  // ---- Plan operations ---------------------------------------------------
  patchWarPlan: (patch: PatchWarPlanInput) => WarPlanDTO;
  patchOperationPlan: (patch: PatchOperationPlanInput) => OperationPlanDTO;
  // ---- Lifecycle ---------------------------------------------------------
  /** Clear every object / arrow + reset the plan for the given flavour. */
  resetFlavour: (flavour: MapMode) => void;
  /** Replace the flavour state in bulk (used for clone / import). */
  replaceFlavour: (flavour: MapMode, slice: SandboxSlice) => void;
}

function nowIso(): string {
  return new Date().toISOString();
}

function newId(): string {
  // crypto.randomUUID is available in evergreen browsers and Node ≥ 16.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Defensive fallback (shouldn't run in the supported browser matrix).
  return 'sbx-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function makeEmptySlice(flavour: MapMode): SandboxSlice {
  const ts = nowIso();
  return {
    objects: [],
    arrows: [],
    warPlan: {
      ...DEFAULT_WAR_PLAN,
      title: flavour === 'planner' ? 'Sandbox War-Plan' : DEFAULT_WAR_PLAN.title,
      updatedAt: ts,
      updatedBy: null,
    },
    operationPlan: {
      ...DEFAULT_OPERATION_PLAN,
      title: flavour === 'operation' ? "Sandbox d'opération" : DEFAULT_OPERATION_PLAN.title,
      updatedAt: ts,
      updatedBy: null,
    },
  };
}

const STORAGE_KEY = 'warplan.sandbox';

export const useSandboxStore = create<SandboxStore>()(
  persist(
    (set, get) => ({
      planner: makeEmptySlice('planner'),
      operation: makeEmptySlice('operation'),

      createObject: (flavour, input) => {
        const created: MapObjectDTO = {
          id: newId(),
          kind: input.kind,
          position: { ...input.position },
          metadata: { ...input.metadata },
          mode: flavour,
          updatedAt: nowIso(),
          deletedAt: null,
        };
        set((s) => ({
          [flavour]: { ...s[flavour], objects: [...s[flavour].objects, created] },
        }));
        return created;
      },

      patchObject: (flavour, id, patch) => {
        let updated: MapObjectDTO | null = null;
        set((s) => {
          const next = s[flavour].objects.map((o) => {
            if (o.id !== id) return o;
            updated = {
              ...o,
              ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
              ...(patch.position !== undefined ? { position: patch.position } : {}),
              ...(patch.metadata !== undefined ? { metadata: patch.metadata } : {}),
              updatedAt: nowIso(),
            };
            return updated;
          });
          return { [flavour]: { ...s[flavour], objects: next } };
        });
        return updated;
      },

      deleteObject: (flavour, id) => {
        set((s) => ({
          [flavour]: {
            ...s[flavour],
            objects: s[flavour].objects.filter((o) => o.id !== id),
          },
        }));
      },

      createArrow: (flavour, input) => {
        const created: MapArrowDTO = {
          id: newId(),
          kind: input.kind,
          points: input.points.map((p) => ({ x: p.x, y: p.y })),
          color: input.color ?? null,
          arrowheadStart: input.arrowheadStart ?? false,
          arrowheadEnd: input.arrowheadEnd ?? true,
          dashed: input.dashed ?? false,
          metadata: { ...(input.metadata ?? {}) },
          mode: flavour,
          updatedAt: nowIso(),
          deletedAt: null,
        };
        set((s) => ({
          [flavour]: { ...s[flavour], arrows: [...s[flavour].arrows, created] },
        }));
        return created;
      },

      patchArrow: (flavour, id, patch) => {
        let updated: MapArrowDTO | null = null;
        set((s) => {
          const next = s[flavour].arrows.map((a) => {
            if (a.id !== id) return a;
            updated = {
              ...a,
              ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
              ...(patch.points !== undefined ? { points: patch.points } : {}),
              ...(patch.color !== undefined ? { color: patch.color } : {}),
              ...(patch.arrowheadStart !== undefined
                ? { arrowheadStart: patch.arrowheadStart }
                : {}),
              ...(patch.arrowheadEnd !== undefined
                ? { arrowheadEnd: patch.arrowheadEnd }
                : {}),
              ...(patch.dashed !== undefined ? { dashed: patch.dashed } : {}),
              ...(patch.metadata !== undefined ? { metadata: patch.metadata } : {}),
              updatedAt: nowIso(),
            };
            return updated;
          });
          return { [flavour]: { ...s[flavour], arrows: next } };
        });
        return updated;
      },

      deleteArrow: (flavour, id) => {
        set((s) => ({
          [flavour]: {
            ...s[flavour],
            arrows: s[flavour].arrows.filter((a) => a.id !== id),
          },
        }));
      },

      patchWarPlan: (patch) => {
        const current = get().planner.warPlan;
        const next: WarPlanDTO = {
          ...current,
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.warStyle !== undefined ? { warStyle: patch.warStyle } : {}),
          ...(patch.opFrequency !== undefined ? { opFrequency: patch.opFrequency } : {}),
          ...(patch.stockManagement !== undefined
            ? { stockManagement: patch.stockManagement }
            : {}),
          ...(patch.griefingRisk !== undefined ? { griefingRisk: patch.griefingRisk } : {}),
          updatedAt: nowIso(),
        };
        set((s) => ({ planner: { ...s.planner, warPlan: next } }));
        return next;
      },

      patchOperationPlan: (patch) => {
        const current = get().operation.operationPlan;
        const next: OperationPlanDTO = {
          ...current,
          ...(patch.warNumber !== undefined ? { warNumber: patch.warNumber } : {}),
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.date !== undefined ? { date: patch.date } : {}),
          ...(patch.objective !== undefined ? { objective: patch.objective } : {}),
          ...(patch.objectiveDefense !== undefined
            ? { objectiveDefense: patch.objectiveDefense }
            : {}),
          ...(patch.objectiveType !== undefined ? { objectiveType: patch.objectiveType } : {}),
          ...(patch.priorities !== undefined ? { priorities: [...patch.priorities] } : {}),
          updatedAt: nowIso(),
        };
        set((s) => ({ operation: { ...s.operation, operationPlan: next } }));
        return next;
      },

      resetFlavour: (flavour) => {
        set(() => ({ [flavour]: makeEmptySlice(flavour) }));
      },

      replaceFlavour: (flavour, slice) => {
        // Force mode coherence on all objects/arrows we accept from the outside
        // (import path) — prevents accidentally mixing flavours within a slice.
        const ts = nowIso();
        const sanitized: SandboxSlice = {
          objects: slice.objects.map((o) => ({ ...o, mode: flavour })),
          arrows: slice.arrows.map((a) => ({ ...a, mode: flavour })),
          warPlan: { ...slice.warPlan, updatedAt: ts },
          operationPlan: { ...slice.operationPlan, updatedAt: ts },
        };
        set(() => ({ [flavour]: sanitized }));
      },
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      partialize: (s) => ({ planner: s.planner, operation: s.operation }),
    },
  ),
);
